'use client';

import { useState } from 'react';
import Script from 'next/script';
// import Link from 'next/link';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import Input from '@/components/atoms/Input';
import UIButton from '@/components/atoms/UIButtons/UIButton';
import { ArrowDown } from '@/components/atoms/Icons';
import Label from '@/components/atoms/Label';
import HeartsSideBg from '@/assets/images/hearts-side-decoration.webp';
import ThanksImg from '@/assets/images/thanks-decorative.png';
const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export default function ContactForm() {
  // const SUBJECT_OPTIONS = [
  //   'Interested in Becoming a Patron Partner',
  //   'Questions About Hearts',
  //   '990 Tax Document Request',
  //   'Other',
  // ];
  const SUBJECT_OPTIONS = [{
    key: 'i',
    value: 'Interested in Becoming a Patron Partner'
  }, {
    key: 'q',
    value: 'Questions About Hearts'
  }, {
    key: 't',
    value: '990 Tax Document Request'
  }, {
    key: 'h',
    value: 'Technical Issues'
  }, {
    key: 'o',
    value: 'Other'
  }];
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const searchParams = useSearchParams();
  const preselectedSubject = searchParams.get('s');
  const [subjectChanged, setSubjectChanged] = useState(false);

  /* ---------------- Email Validation ---------------- */
  const validateEmail = value => {
    if (!value.trim()) {
      setEmailError('Email is required.');
      return false;
    }
    if (!EMAIL_REGEX.test(value.trim())) {
      setEmailError('Enter a valid email address.');
      return false;
    }
    setEmailError('');
    return true;
  };

  /* ---------------- Other Fields Validation ---------------- */
  const validateForm = () => {
    const e = {};
    if (!formData.name.trim()) e.name = 'Name is required';
    if (!formData.subject) e.subject = 'Subject is required';
    if (!formData.message.trim()) e.message = 'Message is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ---------------- Handlers ---------------- */
  const handleChange = e_0 => {
    const {
      name,
      value: value_0
    } = e_0.target;
    if (name === 'subject' && preselectedSubject) setSubjectChanged(true);
    setFormData(prev => ({
      ...prev,
      [name]: value_0
    }));
    if (errors[name]) {
      setErrors(prev_0 => ({
        ...prev_0,
        [name]: null
      }));
    }
  };
  const handleEmailChange = e_1 => {
    const value_1 = e_1.target.value;
    setFormData(prev_1 => ({
      ...prev_1,
      email: value_1
    }));
    if (emailError) {
      validateEmail(value_1);
    }
  };

  /* ---------------- Submit ---------------- */
  const handleSubmit = async e_2 => {
    e_2.preventDefault();
    const isEmailValid = validateEmail(formData.email);
    const isFormValid = validateForm();
    if (!isEmailValid || !isFormValid || loading) return;
    setLoading(true);
    try {
      window.grecaptcha.ready(async () => {
        try {
          const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
            action: 'contact_form'
          });
          const res = await fetch('/api/contact/add', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              ...formData,
              reCaptchaToken: token
            })
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data?.message || 'Submission failed');
          setSuccess(true);
        } catch (err_0) {
          console.error(err_0);
          alert('Submission failed');
        } finally {
          setLoading(false);
        }
      });
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* -------------------- Success State -------------------- */
  if (success) {
    return <div className="xl:min-h-[80vh] min-h-[70vh]">
        <div className="container-wrap flex 2xl:py-30 lg:py-25 py-15 justify-center">
          <div className="max-w-md w-full text-center">
            <div>
              <Image src={ThanksImg} alt="Thank You" width="198" height="134" className="mx-auto sm:max-w-none max-w-[132px]" />
            </div>
            <h1 className="font-semibold text-[#323232] 2xl:text-[68px] xl:text-[54px] lg:text-[48px] sm:text-[40px] text-[38px] leading-[1em] mb-3">
              Thank You!
            </h1>
            <p className="lg:text-lg text-base text-[#626262] mb-6">
              Thank you for your message. <br /> We will be in contact soon
            </p>
            <UIButton href="/" variant="default">
              Home
            </UIButton>
          </div>
        </div>
      </div>;
  }

  /* -------------------- Form -------------------- */
  return <section className="relative xl:pt-25 pt-23 2xl:pb-20 lg:pb-18 pb-15">
      <div className="absolute pointer-events-none left-auto right-0 -top-6 z-[-1] xl:max-w-[30%] sm:max-w-[30%] max-w-[50%] w-auto">
        <Image src={HeartsSideBg} alt="" aria-hidden="true" className="w-full h-auto" priority sizes="100vw" />
      </div>
      <div className="container-wrap w-full">
        <div className="sm:w-[580px] md:pb-20 pb-2 sm:mx-auto">
          <div className="sm:max-w-none max-w-[65vw]">
            <h1 className="font-semibold text-[#323232] 2xl:text-[68px] xl:text-[54px] lg:text-[48px] sm:text-[40px] text-[38px] leading-[1em] mb-3">
              Contact
            </h1>
            <p className="text-base lg:text-lg leading-[1.5] text-[#626262]">
              We would love to hear from you. Just fill out the form below and we will get back with
              you as soon as possible.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="2xl:mt-8 lg:mt-6 mt-4 max-w-[582px] md:space-y-6 space-y-4">
            {/* Name */}
            <div className="md:mb-5 mb-4">
              <Input label="Name*" id="name" labelClass="lg:text-sm text-xs text-[#232323] opacity-70 !leading-[1]" value={formData.name} onChange={handleChange} placeholder="Enter your Name" size="large" name="name" className="h-11 !text-base text-[#232323]" error={!!errors.name} errorMessage={errors.name} />
            </div>

            {/* Email */}
            <div className="md:mb-5 mb-4">
              <Input id="email" label="Email Address*" labelClass="lg:text-sm text-xs text-[#232323] opacity-70 !leading-[1]" type="email" name="email" value={formData.email} onChange={handleEmailChange} onBlur={() => validateEmail(formData.email)} error={!!emailError} errorMessage={emailError} size="large" className="h-11 !text-base text-[#232323]" />
            </div>

            {/* Subject */}
            <div className="md:mb-5 mb-4">
              <Label htmlFor="subject" className="lg:text-sm text-xs text-[#232323] opacity-70 !leading-[1]">
                Subject*
              </Label>
              <div className="relative">
                <select name="subject" id="subject" value={preselectedSubject && !subjectChanged ? SUBJECT_OPTIONS.findLast(x => x.key == preselectedSubject).value : formData.subject} onChange={handleChange} className={`w-full rounded-lg appearance-none border ${errors.subject ? 'border-red-600' : 'border-[#DDDDDD]'} ${!formData.subject && !preselectedSubject ? 'text-gray-400' : 'text-[#232323]'} bg-white pl-4 leading-[1] pr-10 py-2 h-11 text-base font-medium  outline-1 -outline-offset-1 outline-gray-300 focus:outline-1 focus:-outline-offset-1 focus:outline-[#00EAFD]`}>
                  <option value="" disabled hidden>
                    Select a subject
                  </option>
                  {SUBJECT_OPTIONS.map(subject => <option key={subject.key} value={subject.value}>
                      {subject.value}
                    </option>)}
                </select>
                <ArrowDown className="pointer-events-none absolute inset-y-0 right-4 m-auto" />
              </div>

              {errors.subject && <p className="text-xs text-red-500 mt-1">{errors.subject}</p>}
            </div>

            {/* Message */}
            <div className="mb-5">
              <Input label="Message*" id="message" type="textarea" name="message" labelClass="lg:text-sm text-xs text-[#232323] opacity-70 !leading-[1]" rows={4} value={formData.message} onChange={handleChange} placeholder="Enter message..." size="large" className="h-52 !text-base text-[#232323] resize-none" error={!!errors.message} errorMessage={errors.message} />
            </div>

            {/* reCAPTCHA note */}
            {/* <p className="text-xs text-gray-500 mb-5">
              This site is protected by reCAPTCHA and the Google{' '}
              <Link href="https://policies.google.com/privacy" className="underline">
                Privacy Policy
              </Link>{' '}
              and{' '}
              <Link href="https://policies.google.com/terms" className="underline">
                Terms of Service
              </Link>{' '}
              apply.
             </p> */}

            <UIButton type="submit" disabled={loading} rounded="full" variant={loading ? 'default' : 'primaryTheme'} className={`sm:w-auto w-full !px-8 !py-3 mt-1 !text-base ${loading ? 'cursor-not-allowed hover:bg-black/30 hover:text-white' : ''}`}>
              {loading ? 'Submitting...' : 'Submit Message'}
            </UIButton>
          </form>
        </div>
      </div>

      {/* Load reCAPTCHA */}
      <Script id="recaptcha" src={`https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`} strategy="afterInteractive" />
    </section>;
}